import type { Job } from '@/domains/jobs/components/JobCard';
import type { PlannerEvent } from '@/domains/planner';

/** Key for one member's row inside one team: a user can belong to several teams. */
export function memberJobKey(teamId: string, userId: number): string {
  return `${teamId}:${userId}`;
}

/**
 * Join planner events (one per job and member) onto the jobs already on screen.
 * Each event yields a copy of its job carrying that member's own booking times,
 * so bars follow the member's schedule. Day keys are the UTC date of the event, the
 * same as the team bars (the backend derives `scheduled_date` via `toISOString()`), so the
 * two agree; both are off by one for bookings between 00:00 and 01:00 London during BST.
 * Events whose job is not in `jobs` (filtered, not 'Planned', page still loading) get no bar.
 */
export function buildMemberJobs(events: PlannerEvent[], jobs: Job[]): Map<string, Job[]> {
  const jobsById = new Map<string, Job>();
  for (const job of jobs) jobsById.set(job.id, job);

  const byMember = new Map<string, Job[]>();
  for (const event of events) {
    if (event.jobId === null || event.teamId === null || !event.start || !event.end) continue;
    const job = jobsById.get(String(event.jobId));
    if (!job) continue;

    const teamId = String(event.teamId);
    const key = memberJobKey(teamId, event.userId);
    const memberJob: Job = {
      ...job,
      teamId,
      scheduledDate: event.start.slice(0, 10),
      scheduledEndDate: event.end.slice(0, 10),
      startTime: event.start,
      endTime: event.end,
    };
    const list = byMember.get(key);
    if (list) list.push(memberJob);
    else byMember.set(key, [memberJob]);
  }
  return byMember;
}
